import { ReactNode, Fragment } from "react";

import { isVaultTemporarilyUnavailable, LendingVaultExpanded, UserVaultPositionExpanded } from "@bridgesplit/abf-react";
import { AppDialog, useAppDialog } from "app/utils";
import { getLendingVaultPath } from "app/components/common";
import { useNavigate } from "react-router-dom";
import { Button, MenuItems, PopoverWrapper, Row, SPACING, usePopover } from "@bridgesplit/ui";
import { AutoAwesomeOutlined, ExpandMore, VisibilityOutlined } from "@mui/icons-material";

import { VaultExternalRewardsClaimCta } from "../external-rewards";

type VaultAction = {
    label: string;
    component?: ReactNode;
    icon?: ReactNode;
    hide?: boolean;
    hideDialogHeader?: boolean;
    onClick?: () => void; //Callback for when the button is a MenuItem
};

export function VaultActions({
    vaultPosition,
    vaultExpanded,
    hasUnclaimedVaultRewards,
    isCondensed,
    maxItems = 2
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    hasUnclaimedVaultRewards: boolean;
    isCondensed: boolean;
    maxItems?: number;
}) {
    const maxButtons = isCondensed ? 1 : maxItems;
    const fullWidth = isCondensed ? true : false;
    const ctas = useVaultActions({
        vaultPosition,
        hasUnclaimedVaultRewards,
        fullWidth,
        vaultExpanded: vaultExpanded
    });
    const { open: openPopover, popoverProps, close } = usePopover();

    if (!ctas?.length) return null;

    const visibleCtas = ctas.slice(0, maxButtons);
    const extraCtas = ctas.slice(maxButtons);

    const menuItems = extraCtas.map((cta) => ({
        text: cta.label,
        icon: cta.icon,
        onClick: () => {
            close();
            cta.onClick?.();
        }
    }));

    return (
        <Row spacing={1}>
            {visibleCtas.map((cta, i) =>
                cta.component ? (
                    <Fragment key={`visible-cta-${cta.label}-${i}`}>{cta.component}</Fragment>
                ) : (
                    <Button
                        key={`visible-cta-${cta.label}-${i}`}
                        fullWidth={fullWidth}
                        sx={{ minWidth: "max-content" }}
                        variant={(() => {
                            if (isCondensed) return "contained";
                            if (i === 0) return "contained";
                            return "outlined";
                        })()}
                        disabled={!vaultExpanded}
                        onClick={cta.onClick}
                    >
                        {cta.label}
                    </Button>
                )
            )}

            {menuItems.length === 1 && (
                <Button
                    fullWidth={fullWidth}
                    sx={{ minWidth: "max-content" }}
                    variant="outlined"
                    disabled={!vaultExpanded}
                    onClick={menuItems[0].onClick}
                >
                    {menuItems[0].text}
                </Button>
            )}

            {menuItems.length > 1 && (
                <>
                    <Button
                        onClick={openPopover}
                        variant="outlined"
                        fullWidth={fullWidth}
                        sx={{ minWidth: "max-content" }}
                        disabled={!vaultExpanded}
                    >
                        More <ExpandMore />
                    </Button>
                    <PopoverWrapper
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ horizontal: "left", vertical: -SPACING }}
                        {...popoverProps}
                    >
                        <MenuItems close={close} items={menuItems} />
                    </PopoverWrapper>
                </>
            )}
        </Row>
    );
}

export function useVaultActions({
    vaultPosition,
    vaultExpanded,
    hasUnclaimedVaultRewards,
    fullWidth
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    hasUnclaimedVaultRewards: boolean;
    fullWidth: boolean;
}): VaultAction[] {
    const actions: VaultAction[] = [];
    const { open } = useAppDialog();
    const navigate = useNavigate();
    const isActive = vaultPosition?.active;

    if (!vaultExpanded) return actions;

    if (isActive) {
        actions.push({
            label: "Deposit/Withdraw",
            onClick: () => open(AppDialog.VaultManageEscrow, { vaultExpanded: vaultExpanded, side: "deposit" })
        });
    }

    if (hasUnclaimedVaultRewards) {
        actions.push({
            label: "Claim rewards",
            onClick: () => open(AppDialog.VaultClaimRewards, { vaultExpanded: vaultExpanded }),
            component: (
                <VaultExternalRewardsClaimCta
                    vaultExpanded={vaultExpanded}
                    fullWidth={fullWidth}
                    variant={isActive ? "outlined" : "contained"}
                />
            ),
            icon: <AutoAwesomeOutlined />
        });
    }
    if (!isVaultTemporarilyUnavailable(vaultExpanded)) {
        actions.push({
            label: "View vault",
            icon: <VisibilityOutlined />,
            onClick: () => navigate(getLendingVaultPath(vaultExpanded))
        });
    }

    return actions;
}
